import { useMemo } from 'react';
import {
  localizedCode,
  localizedName,
  POSITION_CODES,
  WEIGHT_MAX,
  WEIGHT_SECONDARY,
} from '../data/positions.ts';
import { PLAYER_NOTES } from '../data/squad.ts';
import {
  age,
  decimal,
  isForeign,
  money,
  SEASON_YEAR,
  suitability,
  YOUNG_FOREIGN_BIRTH_YEAR,
} from '../lib/rules.ts';
import { useSquad } from '../lib/squadContext.ts';
import { C, HEADER } from '../lib/theme.ts';

/** The selected player's card, plus the vertical rail shown when the panel is shut. */
export default function PlayerCard() {
  const { state, dispatch, selected, posterName } = useSquad();
  const open = state.playerPanelOpen;
  const toggle = () => dispatch({ type: 'togglePanel', panel: 'player' });

  const card = useMemo(() => {
    if (!selected) return null;
    const p = selected;
    const yearsLeft = p.contractUntil - SEASON_YEAR;
    const contribution = p.goals + p.assists;

    return {
      subtitle:
        p.nationality +
        ' · ' +
        p.positions.map((m) => localizedName(m)).join(', ') +
        ' · ' +
        (isForeign(p) ? 'YABANCI' : 'YERLİ'),
      age: age(p),
      marketValue: money(p.marketValue),
      wage: money(p.wage),
      contract: p.contractUntil,
      // A contract with a year or less left is flagged in the accent colour.
      contractColor: yearsLeft <= 1 ? C.deep : C.ink,
      bars: [
        {
          label: 'REYTİNG',
          value: decimal(p.rating, 2),
          ratio: ((p.rating - 6) / 2) * 100 + '%',
          bg: C.ink,
        },
        {
          label: 'DAKİKA',
          value: p.minutes.toLocaleString('tr-TR'),
          ratio: Math.min(100, (p.minutes / 4200) * 100) + '%',
          bg: C.ink,
        },
        {
          label: 'GOL KATKISI',
          value: String(contribution),
          ratio: Math.min(100, (contribution / 40) * 100) + '%',
          bg: C.red,
        },
        {
          label: 'DEĞER',
          value: money(p.marketValue),
          ratio: Math.min(100, (p.marketValue / 75) * 100) + '%',
          bg: C.ink,
        },
        {
          label: 'MAAŞ YÜKÜ',
          value: money(p.wage),
          ratio: Math.min(100, (p.wage / 12) * 100) + '%',
          bg: C.red,
        },
      ],
      // Heaviest first, so the card reads as a ranking rather than a fixed list of codes.
      positions: POSITION_CODES.filter((code) => suitability(p, code) > 0)
        .sort((a, b) => suitability(p, b) - suitability(p, a))
        .map((code) => {
          const fit = suitability(p, code);
          const natural = fit >= WEIGHT_SECONDARY;
          return {
            code,
            label: localizedCode(code),
            weight: fit,
            bg: natural ? C.ink : C.bg,
            fg: natural ? C.bg : C.ink,
            badgeBg: natural ? C.gold : C.ink,
            badgeFg: natural ? C.ink : C.bg,
          };
        }),
      note:
        PLAYER_NOTES[p.name] ||
        (p.isNew
          ? 'Simülasyona eklenen oyuncu. Kural etkileri anlık olarak yeniden hesaplandı.'
          : isForeign(p)
            ? p.birthYear >= YOUNG_FOREIGN_BIRTH_YEAR
              ? '2003 ve sonrası doğumlu yabancı — +4 kontenjanına yazılabilir.'
              : 'Yabancı kontenjanının ilk 10 hakkını tüketir.'
            : p.homegrown === 'kulup'
              ? "Kulüp altyapısından yetişti — UEFA Liste A kotasında hem 8'lik hem 4'lük şarta sayılır."
              : 'Yerli oyuncu; yabancı kontenjanını etkilemez.'),
    };
  }, [selected]);

  const railBadge = selected ? posterName(selected) : 'SEÇİM YOK';

  return (
    <section
      style={{
        gridColumn: '3',
        order: 3,
        borderRight: `2px solid ${C.ink}`,
        borderBottom: `2px solid ${C.ink}`,
        background: C.bg,
        minWidth: 0,
      }}
    >
      {!open && (
        <div
          onClick={toggle}
          title="Oyuncu kartını aç"
          style={{
            cursor: 'pointer',
            padding: '10px 0 16px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 12,
            minHeight: 440,
            background: C.surface,
            borderBottom: `2px solid ${C.ink}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 800, color: C.red, lineHeight: 1 }}>«</span>
          <span
            style={{
              writingMode: 'vertical-rl',
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.16em',
            }}
          >
            OYUNCU KARTI
          </span>
          <span
            style={{
              writingMode: 'vertical-rl',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '5px 2px',
              background: C.ink,
              color: C.bg,
            }}
          >
            {railBadge}
          </span>
        </div>
      )}

      {open && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '9px 10px 9px 16px',
              borderBottom: `2px solid ${C.ink}`,
              background: C.surface,
            }}
          >
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em' }}>
              OYUNCU KARTI
            </span>
            <button
              onClick={toggle}
              style={{
                border: `2px solid ${C.ink}`,
                background: C.bg,
                color: C.ink,
                fontSize: 11,
                fontWeight: 800,
                letterSpacing: '0.08em',
                padding: '4px 9px',
                cursor: 'pointer',
              }}
            >
              GİZLE »
            </button>
          </div>

          {card && selected && (
            <div style={{ borderBottom: `2px solid ${C.ink}` }}>
              <div style={{ padding: '14px 16px', background: C.ink, color: C.bg }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 10,
                  }}
                >
                  <span
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.1,
                    }}
                  >
                    {selected.name}
                  </span>
                  <span style={{ fontSize: 26, fontWeight: 800, color: C.gold, lineHeight: 1 }}>
                    {selected.shirtNo}
                  </span>
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    color: HEADER.muted,
                    marginTop: 6,
                  }}
                >
                  {card.subtitle}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  borderBottom: `1px solid ${C.line}`,
                }}
              >
                <div style={{ padding: '10px 12px', borderRight: `1px solid ${C.line}` }}>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    YAŞ
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800 }}>{card.age}</div>
                </div>
                <div style={{ padding: '10px 12px', borderRight: `1px solid ${C.line}` }}>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    DEĞER
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800 }}>{card.marketValue}</div>
                </div>
                <div style={{ padding: '10px 12px', borderRight: `1px solid ${C.line}` }}>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    MAAŞ
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800 }}>{card.wage}</div>
                </div>
                <div style={{ padding: '10px 12px' }}>
                  <div
                    style={{
                      fontSize: 9,
                      letterSpacing: '0.12em',
                      color: C.muted,
                      fontWeight: 700,
                    }}
                  >
                    SÖZ.
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: card.contractColor }}>
                    {card.contract}
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.line}` }}>
                {card.bars.map((bar) => (
                  <div
                    key={bar.label}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}
                  >
                    <span
                      style={{
                        width: 82,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.06em',
                        color: C.ink2,
                      }}
                    >
                      {bar.label}
                    </span>
                    <span
                      style={{ flex: 1, height: 8, background: '#dcd9d7', position: 'relative' }}
                    >
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: bar.ratio,
                          background: bar.bg,
                        }}
                      ></span>
                    </span>
                    <span
                      style={{
                        width: 38,
                        textAlign: 'right',
                        fontSize: 11,
                        fontWeight: 800,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {bar.value}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{ padding: '12px 16px', borderBottom: `1px solid ${C.line}` }}>
                <div
                  style={{
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    color: C.muted,
                    fontWeight: 700,
                    marginBottom: 8,
                  }}
                >
                  OYNAYABİLDİĞİ MEVKİLER
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {card.positions.map((m) => (
                    <span
                      key={m.code}
                      title={`${localizedName(m.code)} · ağırlık ${m.weight}/${WEIGHT_MAX}`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10,
                        fontWeight: 800,
                        letterSpacing: '0.06em',
                        padding: '4px 6px 4px 8px',
                        background: m.bg,
                        color: m.fg,
                        border: `1px solid ${C.ink}`,
                      }}
                    >
                      {m.label}
                      <span
                        style={{
                          minWidth: 13,
                          textAlign: 'center',
                          fontSize: 9,
                          padding: '1px 0',
                          background: m.badgeBg,
                          color: m.badgeFg,
                        }}
                      >
                        {m.weight}
                      </span>
                    </span>
                  ))}
                </div>
              </div>

              <div
                style={{
                  padding: '12px 16px',
                  fontSize: 11.5,
                  lineHeight: 1.5,
                  color: C.ink2,
                  textWrap: 'pretty',
                }}
              >
                {card.note}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
